import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DialogFooter,
  Form,
  FormField,
  FormInput,
  FormSelect,
  Button,
  useMultiStepForm,
} from "web-design-system";
import {
  useInvestorAddressDetails,
  useUpdateInvestorAddressDetails,
  useCreateInvestorAddressDetails,
  useCountryOptions,
  useForeignAddress,
  useInvestorProfile,
  useSaveForeignAddress,
  useLocationInfo,
  useStateOptions,
} from "creso-core/hooks";
import { AddressInfoSchema, OverseasAddressInfoSchema } from "creso-core/zod";
import { z } from "zod";
import { titleCase } from "creso-core/utils";
import { Client } from "creso-core/types";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";
import { AutofilledData } from "./autofilled-data";
import { useMinorOnboarding } from "./minor-form-data";

const INDIA_OPTIONS = [{ label: "India", value: "IN" }];

/**
 * `overseas` is for NRI clients: any country, a zip code and free-text state.
 * `minor` prefills an empty address from the guardian's.
 */
export function AddressInfo({
  overseas = false,
  minor = false,
}: {
  overseas?: boolean;
  minor?: boolean;
}) {
  const { client } = useClientOnboarding();
  const { previousStep, updateFormData } = useMultiStepForm();
  const { guardian } = useMinorOnboarding();

  const { mutateAsync: updateAddress } = useUpdateInvestorAddressDetails();

  const { mutateAsync: createAddress } = useCreateInvestorAddressDetails();

  const { data: addressDetails } = useInvestorAddressDetails(
    client?.investment_account ?? ""
  );
  const { data: guardianAddress } = useInvestorAddressDetails(
    minor ? (guardian?.client?.investment_account ?? "") : ""
  );
  // NRIs have only an overseas address, saved separately
  const { data: foreignAddress } = useForeignAddress(
    overseas ? (client?.investment_account ?? "") : ""
  );
  const { mutateAsync: saveForeignAddress } = useSaveForeignAddress();
  // TIN is entered in Tax residency, so it's only known once that's saved
  const { data: profile } = useInvestorProfile(
    client?.investment_account ?? "",
    { enabled: overseas }
  );
  const prefill = overseas
    ? foreignAddress && {
        line1: foreignAddress.foreign_address_1,
        line2: foreignAddress.foreign_address_2,
        line3: foreignAddress.foreign_address_3,
        city: foreignAddress.foreign_address_city,
        state: foreignAddress.foreign_address_state,
        postal_code: foreignAddress.foreign_address_pincode,
        country: foreignAddress.foreign_address_country,
      }
    : (addressDetails ?? guardianAddress);

  const stateOptions = useStateOptions();
  const countryOptions = useCountryOptions();

  const onSubmit = async (data: z.infer<typeof AddressInfoSchema>) => {
    if (overseas) {
      try {
        const saved = await saveForeignAddress({
          exists: !!foreignAddress,
          investment_account_guid: client?.investment_account ?? "",
          foreign_address_1: data.line1,
          foreign_address_2: data.line2 ?? "",
          foreign_address_3: data.line3 ?? "",
          foreign_address_city: data.city,
          foreign_address_state: data.state,
          foreign_address_pincode: data.postal_code,
          foreign_address_country: data.country,
          tax_status: client?.tax_status,
          tin: profile?.first_tax_residency?.taxid_number ?? undefined,
        });
        updateFormData({ addressDetails: saved });
      } catch (error) {
        handleError(error, "Failed to save overseas address");
      }
      return;
    }
    const payload = {
      ...data,
      investment_account_guid: client?.investment_account ?? "",
      line2: data.line2 ?? "",
      line3: data.line3 ?? "",
      autofilled: addressDetails?.autofilled ? !isDirty : false,
      autofill_source: addressDetails?.autofill_source ?? null,
    };
    let newAddressDetails = null;
    try {
      if (addressDetails) {
        newAddressDetails = await updateAddress(payload);
      } else {
        newAddressDetails = await createAddress(payload);
      }
      updateFormData({ addressDetails: newAddressDetails });
    } catch (error) {
      handleError(error, "Failed to save address details");
    }
  };

  const form = useForm({
    resolver: zodResolver(
      overseas ? OverseasAddressInfoSchema : AddressInfoSchema
    ),
    values: {
      line1: prefill?.line1 || "",
      line2: prefill?.line2 || "",
      line3: prefill?.line3 || "",
      country: overseas
        ? prefill?.country && prefill.country !== "IN"
          ? prefill.country
          : ""
        : prefill?.country || "IN",
      city: prefill?.city || "",
      state: prefill?.state || "",
      postal_code: prefill?.postal_code || "",
    },
  });

  const { isDirty } = form.formState;

  const { refetch } = useLocationInfo(form.watch("postal_code"), false);

  const handlePostalCodeBlur = async () => {
    const isValid = await form.trigger("postal_code");
    if (isValid) {
      const result = await refetch();
      if (result.data) {
        form.setValue("state", titleCase(result.data.state), {
          shouldValidate: true,
        });
        form.setValue("city", result.data.city, { shouldValidate: true });
      }
    }
  };

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">
        {overseas ? "Overseas address" : "Address information"}
      </span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="line1"
            render={({ field }) => (
              <FormInput placeholder="Address line 1" {...field} />
            )}
          />
          <FormField
            control={form.control}
            name="line2"
            render={({ field }) => (
              <FormInput
                placeholder="Address line 2"
                {...field}
                value={field.value || ""}
              />
            )}
          />
          <FormField
            control={form.control}
            name="line3"
            render={({ field }) => (
              <FormInput
                placeholder="Address line 3"
                {...field}
                value={field.value || ""}
              />
            )}
          />
          <div className="grid grid-cols-2 w-full gap-4">
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormSelect
                  label="Country"
                  placeholder="Select country"
                  {...field}
                  options={overseas ? countryOptions : INDIA_OPTIONS}
                />
              )}
            />
            <FormField
              control={form.control}
              name="postal_code"
              render={({ field }) => (
                <FormInput
                  label={overseas ? "Zip code" : "Postal code"}
                  {...field}
                  // Pincode lookup only knows Indian codes
                  onBlur={overseas ? field.onBlur : handlePostalCodeBlur}
                  value={field.value || ""}
                />
              )}
            />
            <FormField
              control={form.control}
              name="state"
              render={({ field }) =>
                overseas ? (
                  <FormInput
                    label="State"
                    {...field}
                    value={field.value || ""}
                  />
                ) : (
                  <FormSelect label="State" {...field} options={stateOptions} />
                )
              }
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormInput
                  label="City or town"
                  {...field}
                  value={field.value || ""}
                />
              )}
            />
          </div>
          <DialogFooter className="mt-auto flex sm:flex-col gap-4">
            <AutofilledData
              client={client as Client}
              autofillSource={!isDirty ? addressDetails?.autofill_source : null}
            />
            <div className="flex items-center w-full gap-5">
              <Button type="button" variant="outline" onClick={previousStep}>
                Previous
              </Button>
              <Button
                className="flex-1"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                Next
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
